import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { typedQueryClient } from "hooks";
import { DayplannerKeys } from "modules/Dayplanner/queryKeys";
import { ApiInstance } from "utils/Api";

type ReassignMutationPayload = {
  appointment_id: number;
  user_id: number;
  order: number;
};

export const useReassignMutation = () => {
  const { selectedLocation } = useSelectedLocation();

  const mutation = useMutation({
    mutationFn: async (data: ReassignMutationPayload) => ApiInstance.post("/dayplanner/appointments/reassign", data),
    onMutate: data => {
      if (!selectedLocation?.id) throw new Error("invalid location");
      const appointments = typedQueryClient.getQueryData(DayplannerKeys.appointments);
      if (!appointments) return [];

      const appointment = appointments.find(app => app.id === data.appointment_id);
      if (!appointment) return appointments;

      const mechanicId = appointment.assigned_mechanic;
      const mechanicOrder = appointment.assigned_mechanic_order;

      if (!mechanicId || !mechanicOrder) return;

      const updatedAppointments = appointments.map(app => {
        if (app.id === data.appointment_id) return { ...app, assigned_mechanic: data.user_id, assigned_mechanic_order: data.order };

        if (!app.assigned_mechanic_order) return app;

        if (mechanicId !== data.user_id) {
          if (app.assigned_mechanic === mechanicId && app.assigned_mechanic_order > mechanicOrder)
            return { ...app, assigned_mechanic_order: app.assigned_mechanic_order - 1 };

          if (app.assigned_mechanic === data.user_id && app.assigned_mechanic_order >= data.order)
            return { ...app, assigned_mechanic_order: app.assigned_mechanic_order + 1 };
        } else {
          if (mechanicOrder > data.order) {
            if (app.assigned_mechanic === mechanicId && app.assigned_mechanic_order >= data.order && app.assigned_mechanic_order < mechanicOrder)
              return { ...app, assigned_mechanic_order: app.assigned_mechanic_order + 1 };
          } else {
            if (app.assigned_mechanic === mechanicId && app.assigned_mechanic_order <= data.order && app.assigned_mechanic_order > mechanicOrder)
              return { ...app, assigned_mechanic_order: app.assigned_mechanic_order - 1 };
          }
        }

        return app;
      });

      typedQueryClient.setQueryData(DayplannerKeys.appointments, updatedAppointments);

      return appointments;
    },
    onError: (error, _, appointments) => {
      toast.error(error.message);
      if (selectedLocation?.id) typedQueryClient.setQueryData(DayplannerKeys.appointments, appointments ?? []);
    }
  });

  return mutation.mutate;
};
